import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { site } from "@/mocks/site";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电视剧", to: "/category/drama" },
  { label: "电影", to: "/category/movie" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to.split("?")[0]);
  };

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="flex h-16 items-center gap-4 px-4 md:px-6 lg:gap-8">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-play-circle-fill text-xl" />
          </span>
          <span className="font-brand text-xl leading-none text-primary-700">
            {site.shortName}
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                isActive(item.to)
                  ? "bg-primary-100 font-medium text-primary-800"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden max-w-xs flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 focus-within:border-primary-400 md:flex"
        >
          <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
            <i className="ri-search-line text-base" />
          </span>
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索日韩剧集、电影"
            className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
          />
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-3 py-1 text-xs text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          aria-label="打开导航菜单"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-800 lg:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
          <form
            onSubmit={submitSearch}
            className="mb-3 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2"
          >
            <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索日韩剧集、电影"
              className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-500"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-3 py-1 text-xs text-background-50"
            >
              搜索
            </button>
          </form>
          <div className="grid grid-cols-2 gap-2">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-md px-3 py-2.5 text-sm ${
                  isActive(item.to)
                    ? "bg-primary-100 font-medium text-primary-800"
                    : "bg-background-100 text-foreground-800"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}